import { useEffect, useState } from 'react';
import {
    BooleanField,
    Datagrid,
    DateField,
    DeleteButton,
    Edit,
    EditButton,
    FunctionField,
    Labeled,
    List,
    required,
    Show,
    ShowButton,
    SimpleForm,
    SimpleShowLayout,
    TextField,
    TextInput,
    useNotify,
    useRecordContext,
} from 'react-admin';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';

import {
    ADMIN_AI_SCORE_FEEDBACK_RESOURCE,
    fetchAdminAiScoreFeedbackAudio,
    saveBrowserTranslation,
    type AdminAiScoreFeedbackRecord,
} from '../admin/adminAiScoreFeedbackDataProvider';
import { StatusChipField } from '../admin/StatusChipField';

const filters = [
    <TextInput key="search" source="search" label="Search learner, question, or message" alwaysOn />,
    <TextInput key="status" source="status" label="Status" alwaysOn />,
];

function voteSummary(record?: AdminAiScoreFeedbackRecord): string {
    if (!record) return '';
    return [
        `Easy ${Number(record.easyVoteCount ?? 0)}`,
        `Medium ${Number(record.mediumVoteCount ?? 0)}`,
        `Difficult ${Number(record.difficultVoteCount ?? 0)}`,
    ].join(' · ');
}

function majoritySummary(record?: AdminAiScoreFeedbackRecord): string {
    if (!record?.majorityDifficulty) return 'No clear majority';
    return `${String(record.majorityDifficulty)} (${Number(record.majorityDifficultyVoteCount ?? 0)}/${Number(
        record.uniqueDifficultyVoterCount ?? 0,
    )})`;
}

const VoiceFeedbackPlayer = () => {
    const record = useRecordContext<AdminAiScoreFeedbackRecord>();
    const [audioUrl, setAudioUrl] = useState<string>();
    const [error, setError] = useState<string>();

    useEffect(() => {
        let active = true;
        let objectUrl: string | undefined;
        setAudioUrl(undefined);
        setError(undefined);

        if (!record?.hasAudio || !record.aiScoreFeedbackId) {
            return () => {
                active = false;
            };
        }

        fetchAdminAiScoreFeedbackAudio(record.aiScoreFeedbackId)
            .then((blob) => {
                if (!active) return;
                objectUrl = URL.createObjectURL(blob);
                setAudioUrl(objectUrl);
            })
            .catch((caught: unknown) => {
                if (!active) return;
                setError(caught instanceof Error ? caught.message : 'Unable to load voice feedback.');
            });

        return () => {
            active = false;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [record?.aiScoreFeedbackId, record?.hasAudio]);

    if (!record?.hasAudio) {
        return <Typography variant="body2">No voice feedback</Typography>;
    }
    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }
    if (!audioUrl) {
        return <CircularProgress size={22} />;
    }
    return <Box component="audio" controls preload="metadata" src={audioUrl} sx={{ width: '100%', maxWidth: 560 }} />;
};

type LanguageDetectionResult = {
    detectedLanguage: string;
    confidence: number;
};

type BuiltInAiGlobal = typeof globalThis & {
    LanguageDetector?: {
        availability(): Promise<string>;
        create(): Promise<{
            detect(text: string): Promise<LanguageDetectionResult[]>;
            destroy?(): void;
        }>;
    };
    Translator?: {
        availability(options: { sourceLanguage: string; targetLanguage: string }): Promise<string>;
        create(options: { sourceLanguage: string; targetLanguage: string }): Promise<{
            translate(text: string): Promise<string>;
            destroy?(): void;
        }>;
    };
};

async function translateToEnglishInChrome(text: string) {
    const builtInAi = globalThis as BuiltInAiGlobal;
    if (!builtInAi.LanguageDetector || !builtInAi.Translator) {
        throw new Error('Use Chrome 138 or later to translate in the browser.');
    }

    const detectorAvailability = await builtInAi.LanguageDetector.availability();
    if (detectorAvailability === 'unavailable') {
        throw new Error('Chrome language detection is not available on this device.');
    }

    const detector = await builtInAi.LanguageDetector.create();
    try {
        const detected = (await detector.detect(text))[0];
        if (!detected || detected.confidence < 0.4) {
            throw new Error('The language could not be detected confidently. Enter the English translation manually.');
        }
        const sourceLanguage = detected.detectedLanguage;
        if (sourceLanguage.toLowerCase().startsWith('en')) {
            return { translatedMessage: text, sourceLanguageCode: sourceLanguage };
        }

        const translatorAvailability = await builtInAi.Translator.availability({
            sourceLanguage,
            targetLanguage: 'en',
        });
        if (translatorAvailability === 'unavailable') {
            throw new Error(`Chrome cannot translate ${sourceLanguage} to English on this device.`);
        }

        const translator = await builtInAi.Translator.create({
            sourceLanguage,
            targetLanguage: 'en',
        });
        try {
            return {
                translatedMessage: await translator.translate(text),
                sourceLanguageCode: sourceLanguage,
            };
        } finally {
            translator.destroy?.();
        }
    } finally {
        detector.destroy?.();
    }
}

const BrowserTranslation = () => {
    const record = useRecordContext<AdminAiScoreFeedbackRecord>();
    const notify = useNotify();
    const [isTranslating, setIsTranslating] = useState(false);
    const [translation, setTranslation] = useState<string>();
    const [error, setError] = useState<string>();
    const sourceText = String(record?.transcriptionText ?? record?.message ?? '').trim();

    const translate = async () => {
        if (!record || !sourceText || isTranslating) return;
        setIsTranslating(true);
        setError(undefined);
        try {
            const result = await translateToEnglishInChrome(sourceText);
            await saveBrowserTranslation(record, result.translatedMessage, result.sourceLanguageCode);
            setTranslation(result.translatedMessage);
            notify('English translation saved.', { type: 'success' });
        } catch (caught: unknown) {
            const message = caught instanceof Error ? caught.message : 'Unable to translate this feedback.';
            setError(message);
            notify(message, { type: 'warning' });
        } finally {
            setIsTranslating(false);
        }
    };

    if (!sourceText) {
        return (
            <Typography variant="body2">
                Add a voice transcript first. Stored audio cannot be passed directly to Chrome speech recognition.
            </Typography>
        );
    }

    return (
        <Box sx={{ display: 'grid', gap: 1.5, maxWidth: 720 }}>
            <Button variant="outlined" onClick={translate} disabled={isTranslating}>
                {isTranslating ? 'Translating in Chrome…' : 'Translate text to English in Chrome'}
            </Button>
            {error ? <Alert severity="warning">{error}</Alert> : null}
            {translation ? <Alert severity="success">{translation}</Alert> : null}
            <Typography variant="caption" color="text.secondary">
                Runs in supported desktop Chrome and saves the result. No Google Cloud translation request is made.
            </Typography>
        </Box>
    );
};

export const AiScoreFeedbackList = () => (
    <List filters={filters} perPage={10} sort={{ field: 'createdAt', order: 'DESC' }}>
        <Datagrid rowClick="show" bulkActionButtons={false}>
            <TextField source="questionNo" label="Question" />
            <TextField source="examTaskType" label="Task type" />
            <TextField source="userEmail" label="Learner" />
            <TextField source="questionDifficulty" label="Learner vote" />
            <TextField source="currentComplexityRank" label="Current difficulty" />
            <FunctionField label="Latest unique-user votes" render={(record) => voteSummary(record)} />
            <FunctionField label="Majority" render={(record) => majoritySummary(record)} />
            <BooleanField source="isScoringSatisfied" label="Satisfied" />
            <StatusChipField source="status" label="Status" />
            <DateField source="createdAt" showTime />
            <ShowButton />
            <EditButton />
            <DeleteButton />
        </Datagrid>
    </List>
);

export const AiScoreFeedbackShow = () => (
    <Show>
        <SimpleShowLayout>
            <TextField source="aiScoreFeedbackId" label="AI score feedback ID" />
            <TextField source="aiScoringRequestId" label="AI scoring request ID" />
            <TextField source="practiceExamTaskAttemptId" label="Practice attempt ID" />
            <TextField source="examTaskId" label="Exam task ID" />
            <TextField source="questionNo" label="Question" />
            <TextField source="displayTitle" label="Question title" />
            <TextField source="examName" label="Exam" />
            <TextField source="examTaskType" label="Task type" />
            <TextField source="userEmail" label="Learner email" />
            <TextField source="userMobile" label="Learner mobile" />
            <TextField source="questionDifficulty" label="Learner difficulty vote" />
            <TextField source="currentComplexityRank" label="Current question difficulty" />
            <FunctionField label="Latest unique-user votes" render={(record) => voteSummary(record)} />
            <FunctionField label="Majority result" render={(record) => majoritySummary(record)} />
            <BooleanField source="isScoringSatisfied" label="Satisfied with score" />
            <StatusChipField source="status" label="Status" />
            <TextField source="sourceLanguageCode" label="Recorded/written language" />
            <TextField source="message" label="Learner message" />
            <TextField source="transcriptionStatus" label="Transcription status" />
            <TextField source="transcriptionText" label="Voice transcript" />
            <TextField source="translationStatus" label="Translation status" />
            <TextField source="translatedMessage" label="English translation" />
            <TextField source="translationProvider" label="Translation provider" />
            <TextField source="translationError" label="Translation error" />
            <DateField source="translatedAt" label="Translated at" showTime />
            <Labeled label="Free browser translation">
                <BrowserTranslation />
            </Labeled>
            <Labeled label="Voice feedback">
                <VoiceFeedbackPlayer />
            </Labeled>
            <DateField source="difficultyVotedAt" label="Difficulty vote time" showTime />
            <DateField source="createdAt" showTime />
            <DateField source="updatedAt" showTime />
        </SimpleShowLayout>
    </Show>
);

export const AiScoreFeedbackEdit = () => (
    <Edit resource={ADMIN_AI_SCORE_FEEDBACK_RESOURCE}>
        <SimpleForm>
            <TextInput source="aiScoreFeedbackId" label="AI score feedback ID" disabled fullWidth />
            <TextInput source="questionNo" label="Question" disabled fullWidth />
            <TextInput source="userEmail" label="Learner email" disabled fullWidth />
            <TextInput source="questionDifficulty" label="Learner difficulty vote" disabled fullWidth />
            <TextInput source="currentComplexityRank" label="Current question difficulty" disabled fullWidth />
            <TextInput
                source="status"
                label="Status"
                validate={required()}
                helperText="Only the admin review status is updated here."
                fullWidth
            />
            <TextInput source="message" label="Learner message" disabled multiline minRows={5} fullWidth />
            <TextInput
                source="transcriptionText"
                label="Voice transcript"
                helperText="Enter or correct the transcript for recorded voice feedback."
                multiline
                minRows={5}
                fullWidth
            />
            <TextInput
                source="translatedMessage"
                label="English translation"
                helperText="You can enter or correct the English translation."
                multiline
                minRows={5}
                fullWidth
            />
        </SimpleForm>
    </Edit>
);
